<template>
  <UiDropdown ref="dropdown" :close-on-click="false" :disabled="disabled" :placement="placement" class="overflow-hidden"> <!--  || !multiple -->
    <template #default="{ open }">
      <slot name="selector" :isSelected="(Array.isArray(modelValue) ? modelValue.length > 0 : modelValue !== null && modelValue !== undefined)">
        <div ref="selector" :class="[
          'ui-select box-border flex w-full cursor-pointer items-center gap-1 overflow-hidden', {
          'pointer-events-none !cursor-not-allowed': disabled,
          'px-2 py-1': size === 'small',
          'px-3 py-3 !h-[46px]': !size || size === 'medium',
          'px-4 py-5': size === 'large',
          'error': validation?.[fcProp]?.$invalid,
          'active-filter': activeFilter
        }]">
          <!-- <UiIcon v-if="!normalizedData.length && multiple" icon="search" class="text-[--c-primary-800] dark:text-[--c-primary-600]" stroke-width="1.5" /> -->
          <div v-if="!selectedItems.length" class="w-full h-full items-center gap-2 text-[#BBBBBB] truncate">
            {{ placeholder || '' }}
          </div>
          <div v-if="multiple && selectedItems.length" class="w-full max-w-[calc(100%-24px)] flex gap-2 relative select-none">
            <div ref="listItems" class="taglist snap-x flex items-center justify-start gap-2 max-w-[calc(100%-42px)] overflow-x-auto">
              <div v-for="item in selectedItems" :key="item.value" class="snap-start item-tag gap-2 whitespace-nowrap">
                <div class="text-xs">{{ item.label }}</div>
                <div v-if="!disabled" @click.stop="() => removeElement(item.value)">
                  <UiIcon
                    icon="x"
                    size="14px"
                    class="cursor-pointer hover:text-red-400"
                    stroke-width="1.75"
                  />
                </div>
              </div>
            </div>
            <div v-if="selectedItems.length > 0" class="flex items-center justify-center rounded-full px-2 h-6 text-xs absolute right-2 top-0 bg-sky-200 text-sky-600">
              {{ selectedItems.length }}
            </div>
          </div>
          <div v-if="!multiple && (model !== null && model !== undefined)" class="flex grow flex-wrap items-center justify-start gap-2">
            {{ normalizedData.find(el => el.value === selection)?.label }}
          </div>
          <div v-if="!disabled" class="self-end flex gap-2 shrink-0">
            <UiIcon v-if="clearable && (multiple ? model.length : model)" icon="circle-x" class="text-[#FF4980] cursor-pointer " @click.stop="clearSelected" />
            <UiIcon icon="chevron-down" class="transition-all delay-75 cursor-pointer text-neutral-400" :class="{ 'rotate-180': open }" />
          </div>
        </div>
      </slot>
    </template>

    <template #content>
      <div class="p-1" :style="{ width: width ? `${selector?.getBoundingClientRect().width}px` : 'auto' }">
        <div v-if="searchable === 'always' || (['', 'auto', true].includes(searchable) && listCopy.length > 4)" class="flex items-center justify-end gap-2 px-1 mb-2">
          <UiIcon icon="search" size="18px" class="text-[--c-primary-800] dark:text-[--c-primary-600]" />
          <input
            v-model="search"
            ref="inputSearch"
            type="text"
            class="w-full box-border grow border-0 border-b border-b-neutral-400/50 text-neutral-800 bg-transparent pl-2 pr-7 py-1 text-base outline-none dark:text-[#FDFDFD]"
            :placeholder="$t('input.search')"
            @click.stop
          />
          <UiIcon v-if="search" icon="circle-x" size="24px" class="absolute cursor-pointer text-neutral-400" @click.stop="search = ''" />
        </div>
        <div class="max-h-[168px] overflow-auto" @click.stop="() => closeOnClick && !multiple && dropdown && dropdown.close()">
          <template v-if="visibleItems.length">
            <div v-if="isLimitReached" class="px-2 py-1 text-xs text-amber-600 bg-amber-100 rounded-md mb-1 dark:bg-amber-900 dark:text-amber-200">
              {{ $t('feedback.limitReached', { limit: limit }) || `Limite de ${limit} item(ns) atingido` }}
            </div>
            <div v-if="multiple" class="box-border flex h-full flex-col">
              <div>
                <label :class="['item', { 'limit-disabled': isLimitReached }]">
                  <input type="checkbox" v-model="allSelected" :value="true" class="input-checkbox" :disabled="isLimitReached" />
                  {{ $t('placeholder.select', { field: $t('labels.all').toLowerCase() }) }}
                </label>
              </div>
              <div v-for="item in visibleItems" :key="item.value">
                <label :class="['item', { 'limit-disabled': isLimitReached && !model?.includes(item.value) }]">
                  <input 
                    type="checkbox" 
                    v-model="model" 
                    :value="item.value" 
                    class="input-checkbox"
                    :disabled="isLimitReached && !model?.includes(item.value)"
                  />
                  <p class="truncate max-w-80">{{ item.label }}</p>
                </label>
              </div>
            </div>
            <div v-else class="box-border flex h-full flex-col">
              <div v-for="item in visibleItems" :key="item.value">
                <label :class="['item', { 'active': item.value === model }]">
                  <input v-show="false" type="radio" v-model="model" :value="item.value" />
                  <slot name="item" :item="item">
                    <p class="truncate max-w-80">{{ item.label }}</p>
                  </slot>
                </label>
              </div>
            </div>
          </template>
          <div v-else-if="loading" class="flex items-center justify-center gap-2 text-center text-neutral-500 my-1">
            <UiIcon icon="loader" size="20px" stroke-width="1" class="animate-spin" />
            {{ $t('feedback.loading') }}
          </div>
          <div v-else class="text-center text-neutral-500">
            {{ $t('feedback.noFound') }}
          </div>
        </div>
      </div>
    </template>
  </UiDropdown>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import { useElementBounding } from '@vueuse/core'
import { strSearch } from '@/common/util'

import type { Placement } from '@floating-ui/vue'

import UiDropdown from '@/components/ui/dropdown.vue'
import UiIcon from '@/components/ui/icon.vue'

const emit = defineEmits(['change', 'limit-reached'])

const props = withDefaults(
  defineProps<{
    multiple?: boolean
    placeholder?: string
    placement?: Placement
    clearable?: boolean
    searchable?: 'auto' | 'always' | 'never'
    searchableFields?: string[]
    data?: number[] | string[] | any[][] | {
      label: string
      value: number | string
    }[]
    disabled?: boolean
    size?: 'small' | 'medium' | 'large'
    closeOnClick?: boolean
    selectedOnTop?: boolean
    loading?: boolean
    activeFilter?: boolean
    limit?: number
  }>(), {
    searchable: 'auto',
    selectedOnTop: true,
    closeOnClick: true
  })

const model = defineModel<any | any[]>()

// Controlar se o limite foi atingido
const limitReached = ref(false)

// Interceptar e aplicar regra de limite nas mudanças do modelo
watch(
  () => model.value,
  (val, oldVal) => {
    emit('change', val)
    
    // Verificar limite apenas para seleções múltiplas
    if (props.multiple && props.limit && Array.isArray(val)) {
      // Se o novo valor excede o limite
      if (val.length > props.limit) {
        // Restaurar valor anterior e emitir evento
        model.value = oldVal
        limitReached.value = true
        emit('limit-reached', props.limit)
      } else {
        limitReached.value = false
      }
    }
  }
)

// Computed para verificar se o limite foi atingido
const isLimitReached = computed(() => {
  return props.multiple && props.limit && Array.isArray(model.value) && model.value.length >= props.limit
})

const inputSearch = ref<HTMLInputElement>()
const validation = inject('validation', {})
const fcProp = inject('fc-prop', '')
const dropdown = ref<InstanceType<typeof UiDropdown>>()
const listItems = ref<HTMLDivElement | null>()

const showDropdown = ref(false)
const search = ref()
const inputSelectRef = ref<HTMLDivElement>()

const selector = ref()
const { width } = useElementBounding(selector)

const normalizedData = computed(() => {
  return props.data?.map((el) => {
    if (typeof el === 'number' || typeof el === 'string') return { label: el.toString(), value: el.toString() }
    if (typeof el === 'object' && el?.length === 2) return { label: el[1], value: el[0] }
    return el
  }).filter((el) => el && el.label !== 'Espere!' && el.label !== '¡Espere!') || []
})

const listCopy = ref([])

const selection = computed<any>(() => !props.multiple ? model.value : '')

const selectedItems = computed(() => {
  if (props.multiple)
    return normalizedData.value.filter((el) => Array.isArray(model.value) && model.value.includes(el.value)) || []

  let res = normalizedData.value.find((el) => el.value === model.value)
  return res ? [res] : []
})

 
const visibleTags = computed(() => {
  if (props.multiple) {
    return selectedItems.value.length > 2 ? selectedItems.value.slice(0, 2) : selectedItems.value
  }

  return selectedItems.value
})

const allSelected = computed({
  get: () => normalizedData.value.length === model.value?.length,
  set: (val) => {
    model.value = val ? model.value = listCopy.value.map((el) => el.value) : []
  }
})

const visibleItems = computed(() =>
  search.value
    ? listCopy.value.filter((el) => el && !props.searchableFields ? strSearch(el.label, search.value) : props.searchableFields.some((field) => strSearch(el[field], search.value)))
    : listCopy.value
)

const removeElement = (id: string | number) => {
  const index = model.value.findIndex((el) => el === id)
  if (index >= 0) model.value.splice(index, 1)
}

const clearSelected = () => {
  model.value = props.multiple ? [] : null
}

watch(
  () => ({ isOpen: dropdown.value?.isOpen, loading: props.loading }),
  ({ isOpen, loading }) => {
    if (!isOpen || loading) return

    nextTick(() => {
      inputSearch.value?.focus()
    })

    let list = normalizedData.value

    // Move os itens selecionados para o topo da lista (se a propriedade selectedOnTop for true)
    if (props.selectedOnTop && props.multiple && Array.isArray(model.value) && list.length > 0) {
      let selItems = []
      let unseltems = []

      list.forEach((el) => {
        if (typeof el?.value === 'undefined') return

        if (model?.value.includes(el?.value)) {
          selItems.push(el)
        } else {
          unseltems.push(el)
        }
      })

      list = selItems.concat(unseltems)
    }

    return listCopy.value = list
  }
)

const clickOutside = (e) => {
  if (showDropdown.value && !inputSelectRef.value.contains(e.target)) showDropdown.value = false
}

onMounted(() => {
  document.addEventListener('click', clickOutside, false)
})

onUnmounted(() => {
  document.removeEventListener('click', clickOutside, false)
})
</script>

<style scoped lang="scss">
.ui-select {
  @apply w-full box-border rounded-md border border-neutral-400/50 bg-[#fdfdfd] text-sm leading-5 text-[#212121]
  outline-none transition-all placeholder:text-[#bbb] focus:border-primary disabled:text-[#79767A] dark:border-[#2C2C2C] dark:bg-transparent
  dark:text-[#FDFDFD] dark:focus:border-primary dark:disabled:bg-[#414141];
}
.list-items {
  @apply absolute flex flex-col mt-2 box-border max-h-[240px] h-auto w-full origin-top-right left-0 rounded-lg
  border-[1px] border-neutral-400/50 bg-[#FDFDFD] dark:border-none dark:border-[#2C2C2C] dark:bg-[#2C2C2C] dark:text-[#FDFDFD];
}
.item-tag {
  @apply flex items-center justify-center rounded-md bg-[--c-primary-400] py-1 px-2 text-xs
  font-semibold text-[--c-primary-800];
}
.item {
  @apply flex items-center justify-start gap-2 rounded-md px-2 py-3 cursor-pointer text-sm leading-4 text-[#666666] dark:text-white hover:bg-neutral-200/60 dark:hover:bg-neutral-600;

  .input-checkbox {
    @apply shrink-0 accent-[--c-primary-600] m-0 h-[18px] w-[18px] rounded-[4px] border-[1px] border-[#666666];
  }

  &.active {
    @apply bg-[--c-primary-300] text-[--c-primary-700];
  }

  &.limit-disabled {
    @apply cursor-not-allowed opacity-50 hover:bg-transparent dark:hover:bg-transparent;
  }
}

.error {
  @apply border-[#FF4980] bg-[#fcf2f6] dark:bg-[#383838];
}

.active-filter {
  @apply border-emerald-300 bg-[#fcf2f6] dark:bg-[#383838] border-2;
}

*::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

*::-webkit-scrollbar-track {
  background: #e5e5e5;
  margin: 3px 0px;
  border-radius: 6px;
}

*::-webkit-scrollbar-thumb {
  background-color: #a8a8a8;
  border-radius: 6px;
}

.taglist {
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
